import { useMemo } from 'react';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import type { SourceMixByDow } from '../../lib/useCapacityForecast';
import { HelpHint } from '../HelpHint';

// "Your typical week by source" — small companion tile to Week At A
// Glance + 7-day forecast. Renders 7 horizontal bars (Mon → Sun)
// showing the historical walk-in vs appointment mix per day-of-week,
// computed from the same 8-week window the forecast uses. Same source
// of truth feeds both surfaces (via SourceMixByDow on the forecast
// return), so the tile can never visually disagree with the forecast's
// walk-in-driven flags.
//
// Why this exists alongside the recap walk-in % line: the recap shows
// ONE total walk-in % for the whole period; this tile breaks it down
// per day-of-week, which is where the planning insight lives. "80% of
// my Tuesdays are walk-ins but only 30% of my Saturdays" tells the
// barber to weatherproof Tuesdays + invest in rebook discipline on
// Saturdays, where the recap's flat "60% walk-in overall" wouldn't.
//
// Empty states:
//   - mix is null OR every DoW has zero tagged visits → "Tag your
//     cuts as walk-in / appointment to see the pattern" hint
//   - mix has SOME data but the visible DoW has zero tagged →
//     render the bar as a flat gray "unknown" segment (don't lie)

// Display order — Mon first, Sun last. Matches the week-start convention
// the rest of the capacity tile uses (heatmap rows + forecast cards).
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export function SourceMixTile({ mix }: { mix: SourceMixByDow | null }) {
  const t = useT();
  const language = useUI((s) => s.language);
  const dateLocale = language === 'es' ? 'es-US' : 'en-US';

  // Build localized DoW labels once. Anchor on a known week start so
  // toLocaleDateString returns "Mon" / "Lun" cleanly without timezone
  // weirdness.
  const dowLabels = useMemo(() => {
    // Pick any Sunday — JS Date constructor with explicit year/month/day
    // gives us a deterministic Sunday to walk forward from.
    const anchor = new Date(2024, 0, 7); // 2024-01-07 is a Sunday
    const labels: Record<number, string> = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(anchor);
      d.setDate(anchor.getDate() + i);
      labels[i] = d.toLocaleDateString(dateLocale, { weekday: 'short' });
    }
    return labels;
  }, [dateLocale]);

  // Total tagged across all 7 DoWs — drives the empty-state hint.
  const totalTagged = useMemo(() => {
    if (!mix) return 0;
    let n = 0;
    for (let dow = 0; dow < 7; dow++) n += mix[dow]?.tagged ?? 0;
    return n;
  }, [mix]);

  // Overall walk-in % — anchored header stat. Null until any tagging
  // exists so we don't render a meaningless "0%".
  const overallWalkinPct = useMemo(() => {
    if (!mix || totalTagged === 0) return null;
    let walkin = 0;
    for (let dow = 0; dow < 7; dow++) walkin += mix[dow]?.walkin ?? 0;
    return Math.round((walkin / totalTagged) * 100);
  }, [mix, totalTagged]);

  return (
    <div className="rounded-xl border border-line bg-bg-alt p-3 space-y-2.5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500 inline-flex items-center gap-1.5">
            <span>{t('dash.capacity.sourceMix.eyebrow')}</span>
            <HelpHint
              titleKey="dash.capacity.sourceMix.help.title"
              bodyKey="dash.capacity.sourceMix.help.body"
              ariaKey="dash.capacity.sourceMix.help.aria"
            />
          </div>
          <div className="text-[13px] font-bold text-ink-900 dark:text-ink-100 mt-0.5">
            {t('dash.capacity.sourceMix.title')}
          </div>
          {overallWalkinPct != null && (
            <p className="text-[12px] text-ink-700 dark:text-ink-300 mt-1 leading-snug">
              {t('dash.capacity.sourceMix.overall', {
                walkin: overallWalkinPct,
                appt: 100 - overallWalkinPct,
              })}
            </p>
          )}
        </div>
      </div>

      {totalTagged < 5 ? (
        // Empty state — not enough tagged cuts to draw a pattern.
        // Threshold = 5 (any lower and a single tag dominates a DoW).
        <p className="text-[12px] text-ink-500 italic leading-snug">
          {t('dash.capacity.sourceMix.empty')}
        </p>
      ) : (
        <>
          {/* Per-DoW stacked bars — Mon → Sun. Each row: label, bar,
              count. The bar is a flex with two children, widths set
              by the walk-in / appointment counts. Untagged-only DoWs
              show as flat gray so the user can SEE "I never tag Sun." */}
          <div className="space-y-1.5">
            {DISPLAY_ORDER.map((dow) => {
              const slot = mix?.[dow];
              const tagged = slot?.tagged ?? 0;
              const walkinPct = slot?.walkinPct;
              return (
                <div key={dow} className="flex items-center gap-2">
                  <div className="w-8 shrink-0 text-[10px] font-bold tabular-nums text-ink-700 leading-none">
                    {dowLabels[dow]}
                  </div>
                  <div
                    className="flex-1 h-4 rounded overflow-hidden bg-bg flex border border-line/50"
                    role="img"
                    aria-label={
                      tagged === 0
                        ? t('dash.capacity.sourceMix.barAriaUnknown', { day: dowLabels[dow]! })
                        : t('dash.capacity.sourceMix.barAria', {
                            day: dowLabels[dow]!,
                            walkin: walkinPct ?? 0,
                            appt: 100 - (walkinPct ?? 0),
                            tagged,
                          })
                    }
                  >
                    {tagged === 0 ? (
                      <div
                        className="flex-1 h-full"
                        style={{
                          backgroundImage:
                            'repeating-linear-gradient(135deg, rgba(120,120,120,0.18) 0 3px, transparent 3px 7px)',
                        }}
                      />
                    ) : (
                      <>
                        {/* Walk-in slice — left side, amber. Picked to
                            visually echo the "weather-sensitive" colors
                            used elsewhere (storm red is too alarming
                            for a neutral pattern display). */}
                        <div
                          className="h-full bg-amber-400 dark:bg-amber-500 transition-all"
                          style={{ width: `${walkinPct ?? 0}%` }}
                        />
                        {/* Appointment slice — right side, navy/gold.
                            Matches the chart-bar "primary" color so
                            appointments feel like the "anchored
                            revenue" they functionally are. */}
                        <div
                          className="h-full bg-navy-900 dark:bg-gold-500 transition-all"
                          style={{ width: `${100 - (walkinPct ?? 0)}%` }}
                        />
                      </>
                    )}
                  </div>
                  <div className="w-12 shrink-0 text-right text-[10px] tabular-nums text-ink-500 leading-none">
                    {tagged === 0
                      ? t('dash.capacity.sourceMix.untagged')
                      : `${walkinPct}% WI`}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend — two swatches matching the bar colors. Kept
              compact so the tile reads as a single glance. */}
          <div className="flex items-center gap-3 text-[10px] text-ink-500 pt-1">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-amber-400 dark:bg-amber-500" />
              {t('visitSource.walkin')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-navy-900 dark:bg-gold-500" />
              {t('visitSource.appointment')}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
